import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { appEnv } from '../../../app.config';
import { RoleService } from '../../../core/services/v3/role.service';
import { StateService } from '../../../core/services/state.service';
import { VehicleService } from '../../../core/services/vehicle.service';

export type ContactGroupKey = 'customer' | 'carriers' | 'contractor';

export type CustomerStatus = 'lead' | 'customer';

export interface CrmContact {
  id: number;
  name: string;
  /** Customer - branch line (Customers group only; absent for Carriers/Contractors). */
  company?: string | null;
  /** Customers group only - which side of the pipeline this contact is on. */
  type?: CustomerStatus;
  email: string;
  phone: string | null;
  photo: string | null;
}

export interface ContractorFilters {
  state: string[];
  vehicleTypeId: number[];
  roleId: number[];
  status: number[];
  /** Live-search fallback term - only sent when the already-loaded list came up empty for a typed search. */
  q?: string;
}

export interface CustomerFilters {
  state: string[];
  customerBranchId: number[];
  status: CustomerStatus[];
}

export interface LookupOption {
  id: number | string;
  label: string;
}

export interface ContractorFilterOptions {
  states: LookupOption[];
  vehicleTypes: LookupOption[];
  roles: LookupOption[];
  statuses: LookupOption[];
}

export interface CustomerFilterOptions {
  states: LookupOption[];
  /** Customer - Branch, combined into one filter rather than two. */
  branches: LookupOption[];
  statuses: LookupOption[];
}

export interface EmailAutocompleteResult {
  name: string;
  company?: string | null;
  type?: CustomerStatus;
  email: string;
  phone: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class CrmContactGroupsService {
  constructor(
    private httpClient: HttpClient,
    private stateService: StateService,
    private vehicleService: VehicleService,
    private roleService: RoleService
  ) {}

  getCounts(): Observable<Record<ContactGroupKey, number>> {
    return this.httpClient.get<Record<ContactGroupKey, number>>(`${appEnv.api}crm/messaging/contacts/counts`);
  }

  getCustomers(filters: CustomerFilters): Observable<CrmContact[]> {
    let params = new HttpParams();
    filters.state.forEach((v) => (params = params.append('state[]', v)));
    filters.customerBranchId.forEach((v) => (params = params.append('customer_branch_id[]', String(v))));
    filters.status.forEach((v) => (params = params.append('status[]', v)));

    return this.httpClient
      .get<{ data: CrmContact[] }>(`${appEnv.api}crm/messaging/contacts/customers`, { params })
      .pipe(map((res) => res.data));
  }

  /**
   * States come from the same app-wide StateService Contractors' filter
   * already uses - only the Customer/Branch lookup is CRM-specific enough
   * to need its own request. Customer Status (lead vs existing customer)
   * is a fixed 2-value enum, not a real lookup table, so it's just
   * hardcoded here rather than round-tripped from the backend.
   */
  getCustomerFilterOptions(): Observable<CustomerFilterOptions> {
    return forkJoin({
      states: this.getStates(),
      lookup: this.httpClient.get<{ branches: { id: number; name: string }[] }>(`${appEnv.api}crm/messaging/contacts/customer-filters`)
    }).pipe(
      map(({ states, lookup }) => ({
        states,
        branches: lookup.branches.map((b) => ({ id: b.id, label: b.name })),
        statuses: [
          { id: 'lead', label: 'Lead' },
          { id: 'customer', label: 'Existing Customer' }
        ]
      }))
    );
  }

  getCarriers(): Observable<CrmContact[]> {
    return this.httpClient
      .get<{ data: CrmContact[] }>(`${appEnv.api}crm/messaging/contacts/carriers`)
      .pipe(map((res) => res.data));
  }

  /** `limited` is true when the backend's safety cap (1000 rows) was actually hit - narrow the filters further to see the rest. */
  getContractors(filters: ContractorFilters): Observable<{ data: CrmContact[]; limited: boolean }> {
    let params = new HttpParams();
    filters.state.forEach((v) => (params = params.append('state[]', v)));
    filters.vehicleTypeId.forEach((v) => (params = params.append('vehicle_type_id[]', String(v))));
    filters.roleId.forEach((v) => (params = params.append('role_id[]', String(v))));
    filters.status.forEach((v) => (params = params.append('status[]', String(v))));
    if (filters.q) params = params.append('q', filters.q);

    return this.httpClient.get<{ data: CrmContact[]; limited: boolean }>(`${appEnv.api}crm/messaging/contacts/contractors`, {
      params
    });
  }

  /**
   * State/vehicle-type/role each already have their own reusable, app-wide
   * Angular services (StateService/VehicleService/RoleService, all cached
   * via shareReplay) - reused directly here rather than duplicating HTTP
   * calls. Only driver status has no existing frontend service, since
   * nothing else in the app needed one yet - that's the one real request
   * this makes; the other three come back near-instantly from cache after
   * their first use anywhere in the app.
   */
  getContractorFilterOptions(): Observable<ContractorFilterOptions> {
    return forkJoin({
      states: this.getStates(),
      vehicleTypes: this.getVehicleTypes(),
      roles: this.getRoles(),
      statuses: this.httpClient
        .get<{ statuses: { id: number; name: string }[] }>(`${appEnv.api}crm/messaging/contacts/contractor-filters`)
        .pipe(map((res) => res.statuses.map((s) => ({ id: s.id, label: s.name }))))
    });
  }

  private getStates(): Observable<LookupOption[]> {
    return this.stateService
      .getStateList()
      .pipe(map((res) => (res.data as { id: number; abbr: string; name: string }[]).map((s) => ({ id: s.abbr, label: s.name }))));
  }

  private getVehicleTypes(): Observable<LookupOption[]> {
    return this.vehicleService
      .getVehicleTypes()
      .pipe(
        map((res) =>
          (res.data as { id: number; vehicle_type_name: string }[]).map((v) => ({ id: v.id, label: v.vehicle_type_name }))
        )
      );
  }

  private getRoles(): Observable<LookupOption[]> {
    return this.roleService
      .getList()
      .pipe(map((res) => (res.data as { id: number; name: string }[]).map((r) => ({ id: r.id, label: r.name }))));
  }

  /**
   * Inbox calls this with whichever SMS thread numbers are currently
   * visible - resolves a name for the ones that match a known Driver/
   * Carrier phone (last-9-digit comparison server-side, tolerant of local
   * vs. Ringotel-normalized formats). A number with no match is simply
   * absent from the returned map - the caller falls back to the raw
   * number, sending is never affected either way.
   */
  getPhoneNames(numbers: string[]): Observable<Record<string, string>> {
    let params = new HttpParams();
    numbers.forEach((n) => (params = params.append('numbers[]', n)));

    return this.httpClient
      .get<{ data: Record<string, string> }>(`${appEnv.api}crm/messaging/contacts/phone-lookup`, { params })
      .pipe(map((res) => res.data));
  }

  /** Sidebar unread-SMS badges - which CRM boards (customer/carrier/contractor/supplier)
   *  each number belongs to, so the sidebar doesn't have to load every board in full. */
  getPhoneCategories(numbers: string[]): Observable<Record<string, string[]>> {
    let params = new HttpParams();
    numbers.forEach((n) => (params = params.append('numbers[]', n)));

    return this.httpClient
      .get<{ data: Record<string, string[]> }>(`${appEnv.api}crm/messaging/contacts/phone-categories`, { params })
      .pipe(map((res) => res.data ?? {}));
  }

  /** Compose's "To" field autocomplete - searches the same three tables the group picker uses, by name or email. */
  searchContacts(query: string): Observable<EmailAutocompleteResult[]> {
    return this.httpClient
      .get<{ data: EmailAutocompleteResult[] }>(`${appEnv.api}crm/messaging/contacts/search`, { params: { q: query } })
      .pipe(map((res) => res.data));
  }

  /** Which of these addresses are NOT a known Customer/Carrier/Contractor CRM contact - used to warn before sending to an external recipient. */
  classifyExternal(emails: string[]): Observable<string[]> {
    let params = new HttpParams();
    emails.forEach((e) => (params = params.append('emails[]', e)));

    return this.httpClient
      .get<{ external: string[] }>(`${appEnv.api}crm/messaging/contacts/classify`, { params })
      .pipe(map((res) => res.external));
  }
}
